import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ClipboardList, Plus, Search, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/recruiting/StatusBadge";
import { PriorityBadge, StageBadge } from "@/components/recruiting/PipelineBadges";
import { NewApplicationDialog } from "@/components/recruiting/NewApplicationDialog";
import { EmptyState } from "@/components/common/EmptyState";
import { api, getErrorMessage } from "@/lib/api";
import { STATUSES, formatDate } from "@/lib/constants";

export default function AdminApplications() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [apps, setApps] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [masterData, setMasterData] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const status = searchParams.get("status") || "all";
  const [stage, setStage] = useState("all");
  const [priority, setPriority] = useState("all");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [newAppOpen, setNewAppOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignId, setAssignId] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/applications", { params: {
      status, q: q || undefined, stage, priority,
      include_archived: includeArchived || undefined,
    } })
      .then((res) => setApps(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [status, q, stage, priority, includeArchived]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    api.get("/users/recruiters").then((res) => setRecruiters(res.data.filter((r) => r.active))).catch(() => {});
    api.get("/master-data").then((res) => setMasterData(res.data)).catch(() => {});
  }, []);

  const submitAssign = async () => {
    if (!assignId) return toast.error("Choose a recruiter");
    setBusy(true);
    try {
      await api.post(`/applications/${assignTarget.id}/reassign`, { recruiter_id: assignId });
      toast.success("Recruiter assigned");
      setAssignTarget(null);
      setAssignId("");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">Applications</h1>
          <p className="text-muted-foreground mt-1 text-sm">All applications across jobs. Assign or reassign recruiters — every change is logged.</p>
        </div>
        <Button onClick={() => setNewAppOpen(true)} className="rounded-lg shrink-0" data-testid="admin-new-application-button">
          <Plus className="h-4 w-4 mr-2" /> New Application
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
        <div className="relative flex-1 min-w-56 max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="admin-apps-search" placeholder="Search name, mobile, application ID, job..." className="pl-9 bg-white" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <Select value={stage} onValueChange={setStage}>
          <SelectTrigger className="w-full sm:w-44 bg-white" data-testid="admin-apps-stage-filter">
            <SelectValue placeholder="Filter by stage" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All stages</SelectItem>
            {(masterData.application_stage || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={priority} onValueChange={setPriority}>
          <SelectTrigger className="w-full sm:w-40 bg-white" data-testid="admin-apps-priority-filter">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All priorities</SelectItem>
            {(masterData.application_priority || []).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={(v) => setSearchParams(v === "all" ? {} : { status: v })}>
          <SelectTrigger className="w-full sm:w-52 bg-white" data-testid="admin-apps-status-filter">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
          <Switch checked={includeArchived} onCheckedChange={setIncludeArchived} data-testid="admin-apps-include-archived" />
          Include archived
        </label>
      </div>

      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>
      ) : apps.length === 0 ? (
        <Card className="rounded-xl border-border shadow-none">
          <EmptyState icon={ClipboardList} title="No applications found" description="Applications submitted by members will appear here." testId="admin-apps-empty" />
        </Card>
      ) : (
        <Card className="rounded-xl border-border shadow-none overflow-hidden">
          <div className="overflow-x-auto">
            <Table data-testid="admin-applications-table">
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Candidate</TableHead>
                  <TableHead>Job</TableHead>
                  <TableHead>Stage</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Recruiter</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {apps.map((app) => (
                  <TableRow key={app.id} className={`hover:bg-muted/60 ${app.archived ? "opacity-60" : ""}`} data-testid="admin-application-row">
                    <TableCell className="font-mono text-xs">
                      <p>{app.app_code || app.code}</p>
                      {app.archived && <p className="text-[10px] text-amber-700 font-sans font-medium">Archived</p>}
                    </TableCell>
                    <TableCell>
                      <p className="font-medium">{app.member?.name || app.candidate?.full_name}</p>
                      <p className="text-xs text-muted-foreground font-mono">{app.member?.mobile || app.candidate?.mobile}</p>
                    </TableCell>
                    <TableCell className="text-sm">
                      <p>{app.job?.title}</p>
                      <p className="text-xs text-muted-foreground">{app.job?.hiring_brand}</p>
                    </TableCell>
                    <TableCell><StageBadge stage={app.stage} className="text-[11px]" /></TableCell>
                    <TableCell><PriorityBadge priority={app.priority} className="text-[11px]" /></TableCell>
                    <TableCell className="text-sm">
                      {app.recruiter ? app.recruiter.name : <span className="text-muted-foreground italic">Unassigned</span>}
                    </TableCell>
                    <TableCell><StatusBadge status={app.status} className="text-[11px]" /></TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" className="rounded-lg h-8 bg-white" onClick={() => setAssignTarget(app)} data-testid={`admin-assign-button-${app.id}`}>
                          <UserPlus className="h-3.5 w-3.5 mr-1.5" /> {app.recruiter ? "Reassign" : "Assign"}
                        </Button>
                        <Link to={`/admin/applications/${app.id}`}>
                          <Button variant="ghost" size="sm" className="rounded-lg h-8" data-testid={`admin-view-application-${app.id}`}>View</Button>
                        </Link>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      <NewApplicationDialog open={newAppOpen} onOpenChange={setNewAppOpen} isAdmin onCreated={load} />

      <Dialog open={!!assignTarget} onOpenChange={(v) => !v && setAssignTarget(null)}>
        <DialogContent className="sm:max-w-sm rounded-xl" data-testid="assign-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{assignTarget?.recruiter ? "Reassign recruiter" : "Assign recruiter"}</DialogTitle>
            <DialogDescription>
              {assignTarget?.member?.name || assignTarget?.candidate?.full_name} — {assignTarget?.job?.title} ({assignTarget?.app_code || assignTarget?.code})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Select value={assignId} onValueChange={setAssignId}>
              <SelectTrigger data-testid="assign-dialog-select"><SelectValue placeholder="Choose recruiter..." /></SelectTrigger>
              <SelectContent>
                {recruiters.map((r) => <SelectItem key={r.id} value={r.id}>{r.name} ({r.active_assignments} active)</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={submitAssign} disabled={busy} className="w-full rounded-lg" data-testid="assign-dialog-confirm">
              Confirm assignment
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
